import { createFileRoute } from "@tanstack/react-router";
import { ProductPage } from "@/components/site/ProductPage";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { BrokerVsDirectLender } from "@/components/site/BrokerVsDirectLender";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { buildHead } from "@/lib/seo-head";

const SLUG = "dscr-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG);
const URL = `/${SLUG}`;

const KEYWORDS = [
  "DSCR loan",
  "DSCR mortgage",
  "debt service coverage ratio loan",
  "no income verification rental loan",
  "DSCR loan for LLC",
  "DSCR loan rates",
  "rental property loan no tax returns",
  "DSCR refinance",
  "investment property loan qualify with rent",
  "1-8 unit DSCR loan",
  "DSCR loan requirements",
  "business purpose rental loan",
].join(", ");

export const Route = createFileRoute("/dscr-loans/")({
  head: () =>
    buildHead({
      title: "DSCR Loans: Qualify with Rent, Not Tax Returns",
      description:
        "DSCR rental property loans for 1–8 units, qualified on cash flow — no personal income docs. Up to 80% LTV, LLC-friendly. Get a same-day scenario review.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: PRODUCT.shortName, url: URL },
      ],
      service: { name: PRODUCT.name },
      faqs: PRODUCT.faq.map((f) => ({ q: f.q, a: f.a })),
    }),

  component: () => (
    <ProductPage
      product={PRODUCT}
      topTools={
        <LiveDscrRateSection
          id="live-rates"
          strategyCards
          eyebrow="Live pricing · DSCR"
          heading="Current DSCR Loan Rates for Rental Property"
          paragraphs={[

            "Every figure below comes from a live pricing response under our production loan officer pricing context, priced with lender fees in and on a 30-day lock. The rate, the APR shown for that quote, any points or lender credit and the monthly principal-and-interest payment on a given row all belong to the same quote — we never assemble a row from separate sources or publish a sample rate.",
            "DSCR pricing moves with coverage ratio, leverage, credit tier, property type, purpose and prepayment structure. Two investors buying identical duplexes on the same street can be quoted very differently, which is why the rate center lets you price your own scenario rather than read a static table.",
          ]}
          faqs={[
            { q: "Are the DSCR rates shown here a quote?", a: "No. They are live indications for the scenario that was priced, at the time stamped above. A quote requires a submitted application, credit review and property documentation." },
            { q: "How often does DSCR pricing change?", a: "Investor pricing can change intraday. The timestamp above shows exactly when the displayed pricing was returned and downgrades once it is no longer current." },
          ]}
          links={[
            { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
            { to: "/dscr-loan-rates", label: "DSCR pricing tiers by DSCR, LTV and FICO" },
            { to: "/dscr-calculator", label: "Calculate your DSCR ratio" },
          ]}
        />
      }
      extraTools={
        <>
        <BrokerVsDirectLender />
        <DscrProgramLinks
          currentPath={URL}
          title="DSCR loan programs"
          intro="Purchase, cash-out, rate-and-term, equity lines, and No-Ratio — each structure with its own leverage, credit floor, and pricing."
        />
        </>
      }

    />
  ),
});
